/**
 * Error Contract du Core — `docs/contracts/error-envelope.schema.json` (ADR-009, union de 15 §53 et 27 §18-19).
 *
 * Les catégories et l'erreur elle-même vivent ici parce que tous les paquets en ont besoin ;
 * la correspondance vers un code HTTP appartient à la couche API, pas au Core.
 */
export const ERROR_CATEGORIES = [
    'AUTHENTICATION_ERROR',
    'AUTHORIZATION_ERROR',
    'POLICY_REJECTION',
    'VALIDATION_ERROR',
    'BUSINESS_RULE_VIOLATION',
    'CONFLICT',
    'NOT_FOUND',
    'RATE_LIMITED',
    'TIMEOUT',
    'DEPENDENCY_FAILURE',
    'TEMPORARY_FAILURE',
    'EXECUTION_ERROR',
    'CONFIGURATION_ERROR',
    'MIGRATION_ERROR',
    'INTERNAL_ERROR',
];
export const FAILURE_KINDS = [
    'business',
    'technical',
    'policy_rejection',
    'validation',
    'external_dependency',
    'user_cancellation',
];
const FAILURE_KIND_BY_CATEGORY = {
    AUTHENTICATION_ERROR: 'policy_rejection',
    AUTHORIZATION_ERROR: 'policy_rejection',
    POLICY_REJECTION: 'policy_rejection',
    VALIDATION_ERROR: 'validation',
    BUSINESS_RULE_VIOLATION: 'business',
    CONFLICT: 'business',
    NOT_FOUND: 'business',
    RATE_LIMITED: 'technical',
    TIMEOUT: 'external_dependency',
    DEPENDENCY_FAILURE: 'external_dependency',
    TEMPORARY_FAILURE: 'technical',
    EXECUTION_ERROR: 'technical',
    CONFIGURATION_ERROR: 'technical',
    MIGRATION_ERROR: 'technical',
    INTERNAL_ERROR: 'technical',
};
const RETRYABLE = new Set([
    'RATE_LIMITED',
    'TIMEOUT',
    'DEPENDENCY_FAILURE',
    'TEMPORARY_FAILURE',
]);
export const CORE_SOURCE = 'core';
/** Erreur portant une catégorie du contrat ; jamais un message nu. */
export class AyumiaError extends Error {
    code;
    category;
    details;
    fieldErrors;
    source;
    constructor(category, code, message, options = {}) {
        super(message, options.cause === undefined ? undefined : { cause: options.cause });
        this.name = 'AyumiaError';
        this.category = category;
        this.code = code;
        this.details = options.details;
        this.fieldErrors = options.fieldErrors;
        this.source = options.source ?? CORE_SOURCE;
    }
    get retryable() {
        return RETRYABLE.has(this.category);
    }
    get failureKind() {
        return FAILURE_KIND_BY_CATEGORY[this.category];
    }
}
export function isRetryable(category) {
    return RETRYABLE.has(category);
}
export function failureKindFor(category) {
    return FAILURE_KIND_BY_CATEGORY[category];
}
/**
 * Un tenant manquant ou incohérent est une **erreur de sécurité**, jamais un contexte global
 * (15 §4.2, ADR-011). Ces fabriques existent pour que le message et le code soient les mêmes partout.
 */
export const CoreErrors = {
    tenantMismatch(details) {
        return new AyumiaError('AUTHORIZATION_ERROR', 'TENANT_MISMATCH', "Le tenant demandé n'est pas celui de l'identité authentifiée.", { details });
    },
    entityOutOfScope(details) {
        return new AyumiaError('AUTHORIZATION_ERROR', 'ENTITY_OUT_OF_SCOPE', "L'entité demandée n'est pas dans les scopes de l'identité authentifiée.", { details });
    },
    entityIdsInvalid(message, details) {
        return new AyumiaError('VALIDATION_ERROR', 'ENTITY_IDS_INVALID', message, { details });
    },
    entityRequired(details) {
        return new AyumiaError('VALIDATION_ERROR', 'ENTITY_ID_REQUIRED', "Le tenant possède plusieurs entités : entity_id doit être fourni, il n'est jamais choisi implicitement.", { details });
    },
    reservedField(field) {
        return new AyumiaError('VALIDATION_ERROR', 'RESERVED_ENVELOPE_FIELD', `Le champ d'enveloppe « ${field} » est écrit par le dépôt, jamais par l'appelant.`, { details: { field } });
    },
    notFound(entityType, id) {
        return new AyumiaError('NOT_FOUND', 'DOCUMENT_NOT_FOUND', `${entityType} introuvable dans ce tenant.`, {
            details: { entity_type: entityType, id },
        });
    },
    versionConflict(details) {
        return new AyumiaError('CONFLICT', 'EXPECTED_VERSION_MISMATCH', 'Le document a été modifié depuis votre lecture.', { details });
    },
    invalidSemver(version) {
        return new AyumiaError('VALIDATION_ERROR', 'SEMVER_INVALID', `« ${version} » n'est pas une version sémantique.`, {
            details: { version },
        });
    },
    invalidRange(range) {
        return new AyumiaError('VALIDATION_ERROR', 'SEMVER_RANGE_UNSUPPORTED', `La plage « ${range} » n'appartient pas à la grammaire supportée (exact, ^, ~, joker, comparateurs, ||).`, { details: { range } });
    },
    /** « An invalid model MUST NOT become active » (18 §7) : la construction échoue, elle ne dégrade pas. */
    invalidModel(details) {
        return new AyumiaError('CONFIGURATION_ERROR', 'MODEL_INVALID', "Le modèle est invalide : il ne peut pas devenir actif.", { details });
    },
    unknownObjectType(objectType) {
        return new AyumiaError('CONFIGURATION_ERROR', 'OBJECT_TYPE_UNKNOWN', `Le type « ${objectType} » n'est pas défini dans ce modèle.`, {
            details: { object_type: objectType },
        });
    },
    unknownField(objectType, fieldId) {
        return new AyumiaError('CONFIGURATION_ERROR', 'FIELD_UNKNOWN', `Le champ « ${fieldId} » n'est pas défini sur ${objectType}.`, { details: { object_type: objectType, field_id: fieldId } });
    },
    /** Une version publiée est immuable (15 §9) : on en publie une nouvelle, on ne retouche pas l'ancienne. */
    modelVersionImmutable(details) {
        return new AyumiaError('CONFLICT', 'MODEL_VERSION_IMMUTABLE', "Une version de modèle publiée ne change jamais : publier une nouvelle version.", { details });
    },
    publicationRejected(code, message, details) {
        return new AyumiaError('CONFIGURATION_ERROR', code, message, { details });
    },
    illegalStatusTransition(details) {
        return new AyumiaError('CONFLICT', 'STATUS_TRANSITION_ILLEGAL', "Cette transition de statut n'est pas permise.", {
            details,
        });
    },
};
//# sourceMappingURL=errors.js.map